import { Timestamp } from 'firebase/firestore';

export type MonthYear = {
  month: number;
  year: number;
};

export const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const monthsFullWord = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function convertTimestampToMonthYear(timestamp: Timestamp): MonthYear {
  const date = timestamp.toDate();
  return {
    month: date.getUTCMonth() + 1,
    year: date.getUTCFullYear(),
  };
}

export const convertDateToMonthYear = (date: Date): MonthYear => {
  return {
    month: date.getMonth() + 1, // getMonth() returns month index from 0 (January) to 11 (December)
    year: date.getFullYear(),
  };
};

export function monthYearToString({ month, year }: MonthYear): string {
  // Validate month and year values
  if (month < 1 || month > 12) {
    throw new Error('Invalid month value');
  }
  if (year < 1000 || year > 3000) {
    throw new Error('Invalid year value');
  }
  const yearShort = year.toString().slice(-2); // Get the last two digits of the year
  const monthName = months[month - 1];
  return `${monthName} ${yearShort}`;
}

export const getMonthName = ({ month, year }: MonthYear): string => {
  // Validate month and year values
  if (month < 1 || month > 12) {
    throw new Error('Invalid month value');
  }
  if (year < 1000 || year > 3000) {
    throw new Error('Invalid year value');
  }
  return months[month - 1];
};

export function stringToMonthYear(dateString: string): MonthYear {
  const [monthName, yearShort] = dateString.split(' ');

  // Convert month name to month number
  const month = months.indexOf(monthName) + 1;
  if (month < 1) {
    throw new Error('Invalid month name');
  }

  // Convert two-digit year to full year
  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  let year = currentCentury + parseInt(yearShort, 10);
  if (year > currentYear + 50) {
    year -= 100; // Adjust if the year is more than 50 years in the future
  }

  return { month, year };
}

export function isDate1AfterDate2(date1: MonthYear, date2: MonthYear): boolean {
  // First, compare the years
  if (date1.year > date2.year) {
    return true;
  } else if (date1.year < date2.year) {
    return false;
  }
  // If the years are the same, compare the months
  return date1.month > date2.month;
}

export const isAfter = (date1: MonthYear, date2: MonthYear): boolean =>
  date1.year > date2.year || (date1.year === date2.year && date1.month > date2.month);

export function addMonths({ month, year }: MonthYear, monthsToAdd: number): MonthYear {
  const newMonth = month + monthsToAdd;
  const newYear = year + Math.floor((newMonth - 1) / 12);
  const adjustedMonth = ((newMonth - 1) % 12) + 1;

  return { month: adjustedMonth, year: newYear };
}

export function subtractMonths({ month, year }: MonthYear, monthsToSubtract: number): MonthYear {
  const newMonth = month - monthsToSubtract;
  const newYear = year + Math.floor((newMonth - 1) / 12);
  const adjustedMonth = ((((newMonth - 1) % 12) + 12) % 12) + 1;

  return { month: adjustedMonth, year: newYear };
}

export function changeMonthYearBy(date: MonthYear, monthsToChange: number): MonthYear {
  if (monthsToChange === 0) return date;
  if (monthsToChange < 0) return subtractMonths(date, Math.abs(monthsToChange));
  return addMonths(date, monthsToChange);
}

export const prevQtrMonths = (date: MonthYear): MonthYear[] => {
  const dateQuater = Math.floor((date.month - 1) / 3);
  const prevQuater = dateQuater ? dateQuater - 1 : 3;
  const qtrFirstMonth = prevQuater * 3 + 1;
  const year = qtrFirstMonth === 10 ? date.year - 1 : date.year;

  return Array.from({ length: 3 })
    .map((_, i) => qtrFirstMonth + i)
    .map((month) => ({ month, year }));
};

export function sortMonthYear(dateA: MonthYear, dateB: MonthYear): number {
  return (
    Number(dateA.year + String(dateA.month).padStart(2, '0')) -
    Number(dateB.year + String(dateB.month).padStart(2, '0'))
  );
}

export function createDateRangeDictionary(startYear: number, endYear: number): Record<string, number> {
  const dictionary: Record<string, number> = {};

  for (let year = startYear; year <= endYear; year++) {
    for (let month = 1; month <= 12; month++) {
      dictionary[`${year}${month}`] = 0;
    }
  }

  return dictionary;
}

const getEarliestAndLatestDates = (dates: MonthYear[]): { earliest: MonthYear; latest: MonthYear } | undefined => {
  if (dates.length === 0) return undefined;
  if (dates.length === 1)
    return {
      earliest: dates[0],
      latest: dates[0],
    };

  const compareDates = (a: MonthYear, b: MonthYear): number =>
    a.year !== b.year ? a.year - b.year : a.month - b.month;

  const sortedDates = [...dates].sort(compareDates);

  return {
    earliest: sortedDates[0],
    latest: sortedDates[sortedDates.length - 1],
  };
};

export const getDatesRangeAsString = (dates: MonthYear[]): string => {
  if (dates.length === 0) return '';
  if (dates.length === 1) return months[dates[0].month - 1] + ' ' + dates[0].year.toString();

  const earlyAndLastDates = getEarliestAndLatestDates(dates);

  if (earlyAndLastDates?.earliest && earlyAndLastDates.latest) {
    return (
      months[earlyAndLastDates.earliest.month - 1] +
      ' ' +
      earlyAndLastDates.earliest.year.toString() +
      ' - ' +
      months[earlyAndLastDates.latest.month - 1] +
      ' ' +
      earlyAndLastDates.latest.year.toString()
    );
  }
  return '';
};

export const dateToMonthYear = (myDate: Date): MonthYear => ({
  year: myDate.getFullYear(),
  month: myDate.getMonth() + 1,
});

export function adjustMonthYear(monthYear: MonthYear, value: number): MonthYear {
  const { month, year } = monthYear;
  const totalMonths = month + value;

  const newYear = year + Math.floor((totalMonths - 1) / 12);

  let newMonth = ((totalMonths - 1) % 12) + 1;
  if (newMonth <= 0) {
    newMonth += 12;
  }

  return { month: newMonth, year: newYear };
}

export const isEqualMonthYear = (date1: MonthYear, date2: MonthYear): boolean => {
  return date1.month === date2.month && date1.year === date2.year;
};

export const getCurrentMonthYear = (): MonthYear => {
  const now = new Date();
  return {
    month: now.getMonth() + 1, // getMonth() returns month index from 0 (January) to 11 (December)
    year: now.getFullYear(),
  };
};

export const getQuarterFromMonth = (month: number): number => {
  return Math.ceil(month / 3);
};

export const getQuarterEndMonth = (quarter: number): number => {
  return quarter * 3;
};

export const getQuarterLabel = (month: number, year: number): string => {
  const quarter = getQuarterFromMonth(month);
  const quarterEndMonth = getQuarterEndMonth(quarter);
  const quarterEndDate = new Date(year, quarterEndMonth - 1, 1);
  return quarterEndDate
    .toLocaleDateString('en-US', {
      month: 'short',
      year: '2-digit',
    })
    .replace(' ', '');
};

export const getNextQuarterEndMonths = (startMonth: number, startYear: number, count: number): MonthYear[] => {
  const result: MonthYear[] = [];
  let currentMonth = startMonth;
  let currentYear = startYear;

  for (let i = 0; i < count; i++) {
    const quarter = getQuarterFromMonth(currentMonth);
    const quarterEndMonth = getQuarterEndMonth(quarter);

    if (quarterEndMonth <= 12) {
      result.push({ month: quarterEndMonth, year: currentYear });
    } else {
      result.push({ month: quarterEndMonth - 12, year: currentYear + 1 });
    }

    // Move to next quarter
    currentMonth = quarterEndMonth + 1;
    if (currentMonth > 12) {
      currentMonth = 1;
      currentYear++;
    }
  }

  return result;
};
